import React from 'react';
import {
  getCovidPointedShiftId,
  getIsCovidModeEnabled,
  isUserHasCovidShifts,
} from 'state/Roster/CovidTracing';
import { UserFields } from 'type';
import { RosterUserLegend, RosterRow } from '../../../../components';
import { Avatar } from 'oxygen-elements';
import {
  filtersByTypeSelector,
  rosteredShiftsFilteredSelector,
  timesheetsFilteredSelector,
} from 'state/Roster/RosterFilters';
import { getDurationLabels, getDurationsInfo } from '../../../../helpers';
import { useSelector } from 'react-redux';
import {
  timeOffsByUserIdByDaySelector,
  userByIdRosterDataSelector,
} from 'state/Roster/RosterWeekView';
import { usersBySiteIdSelector } from 'state/UsersCollection';
import { getSiteId } from 'state/RosteredShifts';
import { getSelectedByUser } from 'state/Roster/BulkActions/selectors';
import { getUserName } from '../../../../../../lib/helpers';
import { StoreState } from 'state/types';
import { getCrossLocationTimeOffRosterHours, getCrossLocationTimeOffRosterShiftCount, combineRosterHours } from '../../../../helpers';

type OwnProps = {
  userId: string;
  children: React.ReactNode;
  bulkSelect?: 'user';
  rowIndex?: number;
  legendType: 'single-row' | 'multi-row';
};

type Props = OwnProps;

export const UserRowWithLegend = ({
  children,
  rowIndex,
  userId,
  legendType,
  bulkSelect,
}: Props) => {
  const timeOffs = useSelector(timeOffsByUserIdByDaySelector);
  const filteredRosters = useSelector(rosteredShiftsFilteredSelector);
  const filteredTimesheets = useSelector(timesheetsFilteredSelector);
  const selectedFilter = useSelector(filtersByTypeSelector);
  const siteId = useSelector(getSiteId);
  const rowData = useSelector((state) =>
    userByIdRosterDataSelector(state, userId)
  );
  const selectedByUser = useSelector(getSelectedByUser);
  const contactedClassName = useCovidMarkedRowClassName(userId);
  const user = useSelector(
  (state: StoreState) => {
    const usersAtSite = (usersBySiteIdSelector(state)[siteId] as unknown) as UserFields[] | undefined;
    return usersAtSite?.find((u: UserFields) => u.id === userId);
  }
);

  const getDurations = () => {
    // Get the existing duration info (roster + timesheet hours for the site)
    const duration = getDurationsInfo(
      rowData!.rosters,
      filteredRosters,
      filteredTimesheets,
      timeOffs
    );

    // Add time-off roster hours and shift counts (absences, sickness, etc.) across all sites
    const userTimeOffs = timeOffs[userId] ? timeOffs[userId] : {};
    const timeOffRosterHours = getCrossLocationTimeOffRosterHours(userTimeOffs);
    const timeOffShiftCount = getCrossLocationTimeOffRosterShiftCount(userTimeOffs);
    
    // Add time-off hours and shift counts to the existing roster hours
    const updatedDuration = duration.map((d) => {
      if (d.type === 'roster') {
        return combineRosterHours(d, timeOffRosterHours, timeOffShiftCount);
      }
      return d;
    });

    const contractedTime = user?.contracted_time;

    return getDurationLabels(updatedDuration, selectedFilter, contractedTime);
  };

  const getUserLegendData = () => {
    let label = '';
    let src: string | undefined = '';
    if (user) {
      label = getUserName(user);
      src = user.avatar.src;
    }
    return {
      icon: <Avatar size={'small'} src={src} alt={label} light />,
      label: label,
    };
  };

  const getUserLegend = () => {
    const data = getUserLegendData();
    return (
      <RosterUserLegend
        type={legendType}
        label={data.label}
        icon={data.icon}
        durations={getDurations()}
        onClick={() => false}
        isDisabled={!user || !user.is_active}
        bulkSelect={bulkSelect === 'user'}
        userId={userId}
        pageType={'week'}
        duties={user?.duties}
      />
    );
  };

  return (
    <RosterRow
      isCollapsed={false}
      isSelected={selectedByUser[userId]}
      rowIndex={rowIndex}
      className={contactedClassName}
    >
      {{
        sticky: getUserLegend(),
        content: (
          <div className={`week-rosters-grid spec-cell-height`}>{children}</div>
        ),
      }}
    </RosterRow>
  );
};

export default UserRowWithLegend;

function useCovidMarkedRowClassName(userId: string | null) {
  const hasCovidShifts = useSelector(isUserHasCovidShifts(userId));
  const isCovidMode = useSelector(getIsCovidModeEnabled);
  const markedCovidId = useSelector(getCovidPointedShiftId);

  const isSearchContacted = isCovidMode && markedCovidId;

  return isSearchContacted && (!hasCovidShifts || !userId)
    ? 'no-contacted-shifts'
    : '';
}
